import React, { useState, useEffect, useRef, useCallback } from 'react';
import { MessageCircle, X, Send, ArrowLeft, Loader2, Home, CheckCircle, HelpCircle, ExternalLink, ChevronDown } from 'lucide-react';
import { useT } from '../i18n/useT';
import { useLocation } from 'react-router-dom';

const API = process.env.REACT_APP_BACKEND_URL;

const ChatWidget = () => {
  const location = useLocation();
  const isDashboard = location.pathname.startsWith('/dashboard');
  const [isOpen, setIsOpen] = useState(false);
  const [activeTab, setActiveTab] = useState('home');
  const [view, setView] = useState('home');
  const [sessionId, setSessionId] = useState(() => localStorage.getItem('ob_chat_session') || '');
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState('');
  const [sending, setSending] = useState(false);
  const [email, setEmail] = useState(() => localStorage.getItem('ob_chat_email') || '');
  const [unread, setUnread] = useState(0);
  const messagesEndRef = useRef(null);
  const pollRef = useRef(null);
  const t = useT();
  const ct = t.chat;

  useEffect(() => { messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' }); }, [messages]);

  const ensureSession = useCallback(async () => {
    if (sessionId) return sessionId;
    try {
      const res = await fetch(`${API}/api/chat/session`, { method: 'POST' });
      const data = await res.json();
      localStorage.setItem('ob_chat_session', data.session_id);
      setSessionId(data.session_id);
      return data.session_id;
    } catch { return null; }
  }, [sessionId]);

  const pollMessages = useCallback(async () => {
    if (!sessionId) return;
    try {
      const res = await fetch(`${API}/api/chat/messages/${sessionId}`);
      const data = await res.json();
      const newMsgs = data.messages || [];
      if (newMsgs.length > messages.length) {
        const newCount = newMsgs.filter(m => m.sender === 'admin').length - messages.filter(m => m.sender === 'admin').length;
        if (newCount > 0 && !isOpen) setUnread(prev => prev + newCount);
        setMessages(newMsgs);
      }
    } catch {}
  }, [sessionId, messages.length, isOpen]);

  useEffect(() => {
    if (sessionId) { pollMessages(); pollRef.current = setInterval(pollMessages, 4000); }
    return () => clearInterval(pollRef.current);
  }, [sessionId, pollMessages]);

  useEffect(() => { if (isOpen) setUnread(0); }, [isOpen]);

  const doSend = async (msg) => {
    setSending(true);
    const sid = await ensureSession();
    if (!sid) { setSending(false); return; }
    setInput('');
    setMessages(prev => [...prev, { sender: 'visitor', message: msg, timestamp: new Date().toISOString() }]);
    try {
      await fetch(`${API}/api/chat/send`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ session_id: sid, message: msg, visitor_name: null, visitor_email: email || null }),
      });
    } catch {}
    setSending(false);
  };

  const handleSend = () => { if (input.trim()) doSend(input.trim()); };
  const handleStartAndSend = () => {
    if (!input.trim()) return;
    if (email) localStorage.setItem('ob_chat_email', email);
    doSend(input.trim());
  };

  const showHome = view === 'home' && activeTab === 'home';
  const showMessages = view === 'messages' && activeTab === 'messages';
  const showChat = view === 'chat';
  const needsIntro = !sessionId || (!email && messages.length === 0);

  const quickLinks = [
    { text: ct.viewCourses, href: '#professions' },
    { text: ct.pricingPlans, href: '#information' },
    { text: ct.joinCommunity, href: '/login', membersOnly: true },
  ];

  // ─── BOTTOM NAV (inline) ───
  const bottomNav = (
    <div className="flex items-center justify-around py-2.5 flex-shrink-0" style={{ backgroundColor: '#080b12', borderTop: '1px solid rgba(255,255,255,0.06)' }}>
      <button onClick={() => { setView('home'); setActiveTab('home'); }} data-testid="chat-tab-home"
        className="flex flex-col items-center gap-0.5 px-6 py-1 transition-colors">
        <Home size={18} className={activeTab === 'home' ? 'text-[#E8D5A3]' : 'text-gray-600'} />
        <span className={`text-[11px] font-medium ${activeTab === 'home' ? 'text-[#E8D5A3]' : 'text-gray-600'}`}>{ct.home}</span>
      </button>
      <button onClick={() => { setView('messages'); setActiveTab('messages'); }} data-testid="chat-tab-messages"
        className="flex flex-col items-center gap-0.5 px-6 py-1 transition-colors relative">
        <MessageCircle size={18} className={activeTab === 'messages' ? 'text-[#E8D5A3]' : 'text-gray-600'} />
        <span className={`text-[11px] font-medium ${activeTab === 'messages' ? 'text-[#E8D5A3]' : 'text-gray-600'}`}>{ct.messages}</span>
        {unread > 0 && <span className="absolute -top-0.5 right-3 w-4 h-4 rounded-full text-[9px] font-bold flex items-center justify-center" style={{ backgroundColor: '#E8D5A3', color: '#0d1117' }}>{unread}</span>}
      </button>
    </div>
  );

  if (isDashboard) return null;

  return (
    <div className="fixed bottom-7 right-3 z-[10000]" data-testid="chat-widget">
      {isOpen && (
        <div className="absolute bottom-[68px] right-0 w-[380px] rounded-2xl overflow-hidden"
          data-testid="chat-popup"
          style={{ height: 'min(700px, calc(100vh - 100px))', backgroundColor: '#0d1117', boxShadow: '0 12px 60px rgba(0,0,0,0.5), 0 0 0 1px rgba(255,255,255,0.06)', animation: 'chatSlideUp 0.25s ease-out', display: 'flex', flexDirection: 'column' }}>

          {/* ═══ HOME TAB ═══ */}
          {showHome && (
            <div className="flex flex-col h-full">
              <div className="relative overflow-hidden px-5 pt-5 pb-8" style={{ background: 'linear-gradient(180deg, #080b12 0%, #0d1117 60%, #111827 100%)' }}>
                <div className="absolute inset-0 opacity-[0.04]" style={{ backgroundImage: 'radial-gradient(circle at 30% 40%, #E8D5A3 0%, transparent 60%), radial-gradient(circle at 80% 60%, #E6EBF2 0%, transparent 50%)' }} />
                <div className="flex justify-between items-start relative z-10">
                  <div className="flex items-center gap-2">
                    <div className="w-8 h-8 rounded-lg flex items-center justify-center" style={{ background: 'linear-gradient(135deg, #E8D5A3, #c4a86e)' }}>
                      <span className="text-[10px] font-black text-[#0d1117]" style={{ fontFamily: '"Space Grotesk", sans-serif' }}>OB</span>
                    </div>
                    <div className="flex -space-x-1.5">
                      {[0,1,2].map(i => <div key={i} className="w-7 h-7 rounded-full border-2" style={{ borderColor: '#0d1117', background: ['linear-gradient(135deg,#1a2332,#2a3a52)','linear-gradient(135deg,#1e2a3a,#2c3e56)','linear-gradient(135deg,#1a2332,#253448)'][i] }} />)}
                    </div>
                  </div>
                  <button onClick={() => setIsOpen(false)} data-testid="chat-close" className="p-1.5 rounded-lg transition-colors" style={{ backgroundColor: 'rgba(255,255,255,0.06)' }}><X size={14} className="text-gray-500" /></button>
                </div>
                <h3 className="text-gray-400 text-[15px] mt-5 leading-tight relative z-10" style={{ fontFamily: '"Space Grotesk", sans-serif' }}>{ct.howCanWeHelp}</h3>
                <p className="text-white text-[22px] font-bold leading-tight relative z-10" style={{ fontFamily: '"Space Grotesk", sans-serif' }}>{ct.talkWithUs}</p>
              </div>
              <div className="flex-1 overflow-y-auto px-4 py-3" style={{ backgroundColor: '#0d1117' }}>
                <button onClick={() => { setView('chat'); setActiveTab('messages'); }} data-testid="chat-start-conversation"
                  className="w-full flex items-center gap-3 px-4 py-4 rounded-xl text-left transition-all duration-200 hover:bg-white/[0.04]"
                  style={{ backgroundColor: 'rgba(255,255,255,0.04)', border: '1px solid rgba(255,255,255,0.08)' }}>
                  <div className="flex-1">
                    <p className="text-white text-[14px] font-semibold">{ct.askQuestion}</p>
                    <p className="text-gray-500 text-[12px] mt-0.5">{ct.teamCanHelp}</p>
                  </div>
                  <div className="w-9 h-9 rounded-lg flex items-center justify-center flex-shrink-0" style={{ background: 'linear-gradient(135deg, #E8D5A3, #c4a86e)' }}><HelpCircle size={16} className="text-[#0d1117]" /></div>
                </button>
                <div className="mt-3 rounded-xl overflow-hidden" style={{ backgroundColor: 'rgba(255,255,255,0.03)', border: '1px solid rgba(255,255,255,0.07)' }}>
                  {quickLinks.map((link, i) => (
                    <a key={i} href={link.href} target={link.external ? '_blank' : '_self'} rel="noopener noreferrer"
                      className="flex items-center justify-between px-4 py-3.5 no-underline transition-colors hover:bg-white/[0.03]"
                      style={{ borderBottom: i < 2 ? '1px solid rgba(255,255,255,0.05)' : 'none' }}
                      onClick={() => { setIsOpen(false); }}>
                      <span className="text-gray-300 text-[13px]">{link.text}</span>
                      {link.membersOnly
                        ? <span className="text-[10px] px-1.5 py-0.5 rounded font-bold" style={{ backgroundColor: 'rgba(232,213,163,0.15)', color: '#E8D5A3' }}>VIP</span>
                        : <ExternalLink size={13} className="text-gray-600" />}
                    </a>
                  ))}
                </div>
                <div className="mt-3 rounded-xl px-4 py-3 flex items-center gap-2.5" style={{ backgroundColor: 'rgba(255,255,255,0.03)', border: '1px solid rgba(255,255,255,0.07)' }}>
                  <CheckCircle size={16} className="text-emerald-400 flex-shrink-0" />
                  <span className="text-gray-400 text-[13px]">Status: {ct.statusOk}</span>
                </div>
              </div>
              {bottomNav}
            </div>
          )}

          {/* ═══ MESSAGES TAB ═══ */}
          {showMessages && (
            <div className="flex flex-col h-full" style={{ backgroundColor: '#0d1117' }}>
              <div className="flex items-center justify-between px-5 py-4" style={{ borderBottom: '1px solid rgba(255,255,255,0.07)' }}>
                <h4 className="text-white text-[16px] font-bold" style={{ fontFamily: '"Space Grotesk", sans-serif' }}>{ct.messages}</h4>
                <button onClick={() => setIsOpen(false)} data-testid="chat-close-messages" className="p-1.5 rounded-lg transition-colors" style={{ backgroundColor: 'rgba(255,255,255,0.06)' }}><X size={14} className="text-gray-500" /></button>
              </div>
              <div className="flex-1 flex flex-col items-center justify-center px-6">
                {messages.length === 0 ? (
                  <>
                    <div className="w-14 h-14 rounded-xl flex items-center justify-center mb-3" style={{ backgroundColor: 'rgba(255,255,255,0.05)' }}><MessageCircle size={24} className="text-gray-600" /></div>
                    <p className="text-white text-[16px] font-bold" style={{ fontFamily: '"Space Grotesk", sans-serif' }}>{ct.noMessages}</p>
                    <p className="text-gray-500 text-[13px] text-center mt-1.5">{ct.messagesWillAppear}</p>
                  </>
                ) : (
                  <button onClick={() => setView('chat')} data-testid="chat-open-thread"
                    className="w-full flex items-center gap-3 px-4 py-3.5 rounded-xl hover:bg-white/[0.04] transition-colors text-left"
                    style={{ backgroundColor: 'rgba(255,255,255,0.04)', border: '1px solid rgba(255,255,255,0.08)' }}>
                    <div className="w-10 h-10 rounded-lg flex items-center justify-center flex-shrink-0" style={{ background: 'linear-gradient(135deg, #E8D5A3, #c4a86e)' }}>
                      <span className="text-[10px] font-black text-[#0d1117]">OB</span>
                    </div>
                    <div className="flex-1 min-w-0">
                      <p className="text-white text-[14px] font-semibold truncate">{ct.openBroSupport}</p>
                      <p className="text-gray-500 text-[12px] truncate">{messages[messages.length - 1]?.message}</p>
                    </div>
                    {unread > 0 && <span className="w-5 h-5 rounded-full text-[10px] font-bold flex items-center justify-center flex-shrink-0" style={{ backgroundColor: '#E8D5A3', color: '#0d1117' }}>{unread}</span>}
                  </button>
                )}
              </div>
              <div className="px-5 py-3" style={{ borderTop: '1px solid rgba(255,255,255,0.05)' }}>
                <button onClick={() => { setView('chat'); setActiveTab('messages'); }} data-testid="chat-ask-question-btn"
                  className="mx-auto flex items-center gap-2 px-5 py-2.5 rounded-lg transition-all hover:opacity-90"
                  style={{ background: 'linear-gradient(135deg, #E8D5A3, #c4a86e)' }}>
                  <span className="text-[#0d1117] text-[13px] font-bold">{ct.askQuestion}</span>
                  <HelpCircle size={14} className="text-[#0d1117]" />
                </button>
              </div>
              {bottomNav}
            </div>
          )}

          {/* ═══ CHAT VIEW ═══ */}
          {showChat && (
            <div className="flex flex-col h-full" style={{ backgroundColor: '#0d1117' }}>
              {/* Header */}
              <div className="flex items-center gap-3 px-4 py-3.5 flex-shrink-0" style={{ borderBottom: '1px solid rgba(255,255,255,0.07)' }}>
                <button onClick={() => { setView('home'); setActiveTab('home'); }} data-testid="chat-back" className="p-1.5 rounded-lg transition-colors" style={{ backgroundColor: 'rgba(255,255,255,0.06)' }}>
                  <ArrowLeft size={15} className="text-gray-400" />
                </button>
                <div className="w-8 h-8 rounded-lg flex items-center justify-center flex-shrink-0" style={{ background: 'linear-gradient(135deg, #E8D5A3, #c4a86e)' }}>
                  <span className="text-[9px] font-black text-[#0d1117]">OB</span>
                </div>
                <div className="flex-1">
                  <p className="text-white text-[14px] font-bold" style={{ fontFamily: '"Space Grotesk", sans-serif' }}>{ct.openBroSupport}</p>
                  <div className="flex items-center gap-1.5"><div className="w-1.5 h-1.5 rounded-full bg-emerald-400" /><p className="text-gray-500 text-[11px]">{ct.onlineNow}</p></div>
                </div>
                <button onClick={() => setIsOpen(false)} className="p-1.5 rounded-lg transition-colors" style={{ backgroundColor: 'rgba(255,255,255,0.06)' }}><X size={14} className="text-gray-500" /></button>
              </div>

              {needsIntro ? (
                /* ─── Intro form ─── */
                <div className="flex-1 px-4 py-5 flex flex-col">
                  <div className="flex items-start gap-2.5 mb-6">
                    <div className="w-8 h-8 rounded-lg flex items-center justify-center flex-shrink-0" style={{ background: 'linear-gradient(135deg, #E8D5A3, #c4a86e)' }}>
                      <span className="text-[9px] font-black text-[#0d1117]">OB</span>
                    </div>
                    <div>
                      <div className="rounded-2xl rounded-tl-sm px-4 py-3" style={{ backgroundColor: 'rgba(255,255,255,0.06)', border: '1px solid rgba(255,255,255,0.06)' }}>
                        <p className="text-gray-200 text-[13px] whitespace-pre-line leading-relaxed">{ct.hiMessage}</p>
                      </div>
                      <p className="text-gray-600 text-[10px] mt-1.5 ml-1">{ct.openBroSupport} · {ct.justNow}</p>
                    </div>
                  </div>
                  <div className="mt-auto">
                    <div className="rounded-xl overflow-hidden" style={{ border: '1px solid rgba(232,213,163,0.3)' }}>
                      <input value={email} onChange={e => setEmail(e.target.value)} placeholder={ct.emailPlaceholder} type="email" data-testid="chat-email-input"
                        className="w-full px-4 py-3 text-[13px] text-white placeholder-gray-600 focus:outline-none"
                        style={{ backgroundColor: 'rgba(255,255,255,0.04)', borderBottom: '1px solid rgba(255,255,255,0.06)' }} />
                      <div className="flex items-center" style={{ backgroundColor: 'rgba(255,255,255,0.04)' }}>
                        <input value={input} onChange={e => setInput(e.target.value)}
                          onKeyDown={e => { if (e.key === 'Enter' && !e.shiftKey) { e.preventDefault(); handleStartAndSend(); } }}
                          placeholder={ct.messagePlaceholder} data-testid="chat-input"
                          className="flex-1 px-4 py-3 text-[13px] text-white placeholder-gray-600 bg-transparent focus:outline-none" />
                        <button onClick={handleStartAndSend} disabled={!input.trim() || sending} data-testid="chat-send"
                          className="w-8 h-8 rounded-lg flex items-center justify-center mr-2 transition-all disabled:opacity-20"
                          style={{ backgroundColor: input.trim() ? '#E8D5A3' : 'rgba(255,255,255,0.06)' }}>
                          {sending ? <Loader2 size={14} className="text-[#0d1117] animate-spin" /> : <Send size={13} className={input.trim() ? 'text-[#0d1117]' : 'text-gray-600'} style={{ transform: 'rotate(-45deg)' }} />}
                        </button>
                      </div>
                    </div>
                  </div>
                </div>
              ) : (
                /* ─── Messages ─── */
                <>
                  <div className="flex-1 overflow-y-auto px-4 py-4 space-y-3" data-testid="chat-messages" style={{ minHeight: 0 }}>
                    <div className="flex items-start gap-2.5">
                      <div className="w-7 h-7 rounded-lg flex items-center justify-center flex-shrink-0" style={{ background: 'linear-gradient(135deg, #E8D5A3, #c4a86e)' }}>
                        <span className="text-[8px] font-black text-[#0d1117]">OB</span>
                      </div>
                      <div>
                        <div className="rounded-2xl rounded-tl-sm px-3.5 py-2.5" style={{ backgroundColor: 'rgba(255,255,255,0.06)' }}>
                          <p className="text-gray-200 text-[13px] whitespace-pre-line leading-relaxed">{ct.hiMessage}</p>
                        </div>
                        <p className="text-gray-600 text-[10px] mt-1 ml-1">{ct.openBroSupport} · {ct.justNow}</p>
                      </div>
                    </div>
                    {messages.map((msg, i) => (
                      <div key={i} className={`flex ${msg.sender === 'visitor' ? 'justify-end' : 'items-start gap-2.5'}`}>
                        {msg.sender === 'admin' && (
                          <div className="w-7 h-7 rounded-lg flex items-center justify-center flex-shrink-0" style={{ background: 'linear-gradient(135deg, #E8D5A3, #c4a86e)' }}>
                            <span className="text-[8px] font-black text-[#0d1117]">OB</span>
                          </div>
                        )}
                        <div>
                          <div className="max-w-[240px] px-3.5 py-2.5" style={{
                            backgroundColor: msg.sender === 'visitor' ? 'rgba(232,213,163,0.15)' : 'rgba(255,255,255,0.06)',
                            border: msg.sender === 'visitor' ? '1px solid rgba(232,213,163,0.2)' : '1px solid rgba(255,255,255,0.05)',
                            borderRadius: msg.sender === 'visitor' ? '16px 16px 4px 16px' : '16px 16px 16px 4px',
                          }}>
                            <p className="text-[13px] leading-[1.5] break-words" style={{ color: msg.sender === 'visitor' ? '#E8D5A3' : 'rgba(255,255,255,0.8)' }}>{msg.message}</p>
                          </div>
                          <span className="text-gray-600 text-[10px] block mt-1 ml-1">{msg.timestamp ? new Date(msg.timestamp).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' }) : ''}</span>
                        </div>
                      </div>
                    ))}
                    <div ref={messagesEndRef} />
                  </div>
                  <div className="px-3 py-3 flex-shrink-0" style={{ borderTop: '1px solid rgba(255,255,255,0.07)' }}>
                    <div className="flex items-center gap-2 rounded-xl px-3" style={{ border: '1px solid rgba(255,255,255,0.1)', backgroundColor: 'rgba(255,255,255,0.04)' }}>
                      <input value={input} onChange={e => setInput(e.target.value)}
                        onKeyDown={e => { if (e.key === 'Enter' && !e.shiftKey) { e.preventDefault(); handleSend(); } }}
                        placeholder={ct.messagePlaceholder} data-testid="chat-input-active"
                        className="flex-1 py-3 text-[13px] text-white placeholder-gray-600 bg-transparent focus:outline-none" />
                      <button onClick={handleSend} disabled={!input.trim() || sending} data-testid="chat-send-active"
                        className="w-8 h-8 rounded-lg flex items-center justify-center transition-all flex-shrink-0 disabled:opacity-20"
                        style={{ backgroundColor: input.trim() ? '#E8D5A3' : 'rgba(255,255,255,0.06)' }}>
                        {sending ? <Loader2 size={13} className="text-[#0d1117] animate-spin" /> : <Send size={13} style={{ color: input.trim() ? '#0d1117' : '#4b5563', transform: 'rotate(-45deg)' }} />}
                      </button>
                    </div>
                  </div>
                </>
              )}
            </div>
          )}
        </div>
      )}

      {/* Toggle button */}
      <button onClick={() => setIsOpen(!isOpen)} data-testid="chat-toggle"
        className="w-12 h-12 rounded-full flex items-center justify-center transition-all duration-300 hover:scale-110 relative"
        style={{ background: isOpen ? '#1a1f2e' : 'linear-gradient(135deg, #E6EBF2 0%, #8E98A8 100%)', boxShadow: isOpen ? '0 4px 16px rgba(0,0,0,0.4)' : '0 4px 24px rgba(0,212,255,0.35)' }}>
        {isOpen ? <ChevronDown size={20} className="text-gray-400" /> : <MessageCircle size={20} className="text-white" />}
        {unread > 0 && !isOpen && <span className="absolute -top-1 -right-1 w-5 h-5 rounded-full text-[10px] font-bold flex items-center justify-center" style={{ backgroundColor: '#ef4444', color: '#fff', boxShadow: '0 0 8px rgba(239,68,68,0.5)' }}>{unread}</span>}
      </button>

      <style>{`@keyframes chatSlideUp { from { opacity:0; transform:translateY(12px) scale(0.96); } to { opacity:1; transform:translateY(0) scale(1); } }`}</style>
    </div>
  );
};

export default ChatWidget;
